import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { Snackbar, Alert } from '@mui/material';
import bookmarkService from '../services/bookmarkService';
import { useUserContext } from '../hooks/useUserContext';

interface BookmarkContextValue {
  bookmarkedIds: Set<number>;
  toggle: (courseId: number) => Promise<void>;
  isBookmarked: (courseId: number) => boolean;
}

const BookmarkContext = createContext<BookmarkContextValue>({
  bookmarkedIds: new Set(),
  toggle: async () => {},
  isBookmarked: () => false,
});

export function BookmarkProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUserContext();
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<number>>(new Set());
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!isSignedIn) return;
    bookmarkService.getBookmarks().then((bookmarks) => {
      setBookmarkedIds(new Set(bookmarks.map((b) => b.courseId)));
    });
  }, [isSignedIn]);

  const toggle = useCallback(async (courseId: number) => {
    const wasBookmarked = bookmarkedIds.has(courseId);

    // Optimistic update
    setBookmarkedIds((prev) => {
      const next = new Set(prev);
      if (wasBookmarked) next.delete(courseId);
      else next.add(courseId);
      return next;
    });

    try {
      if (wasBookmarked) {
        await bookmarkService.removeBookmark(courseId);
      } else {
        await bookmarkService.addBookmark(courseId);
      }
    } catch {
      // Revert on error
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        if (wasBookmarked) next.add(courseId);
        else next.delete(courseId);
        return next;
      });
      setErrorMsg('Failed to update bookmark. Please try again.');
    }
  }, [bookmarkedIds]);

  const isBookmarked = useCallback((courseId: number) => bookmarkedIds.has(courseId), [bookmarkedIds]);

  return (
    <BookmarkContext.Provider value={{ bookmarkedIds, toggle, isBookmarked }}>
      {children}
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} variant="filled">
          {errorMsg}
        </Alert>
      </Snackbar>
    </BookmarkContext.Provider>
  );
}

export function useBookmarks() {
  return useContext(BookmarkContext);
}
